import { Box, Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useEducationMaterialShareWith } from "../../../../../queries/education-material/use-education-material-share-with";
import { useSearchPatientList } from "../../../../../queries/list/useSearchPatientList";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  showToastOnApiCall,
  getUnNecessaryTransformData,
  getXtenantId,
} from "../../../../../utils/utils";
import { useState } from "react";
import Flex from "../../../../Flex";
import MaskMultiSelectScroll from "@/components/MaskMultiSelectScroll";
import SideSheetDrawer from "../../../../SidesheetDrawer";
import shareIcon from "/public/share.png";

const initialValues = {
  patients: undefined,
};

const validationSchema = Yup.object({
  patients: Yup.array().of(Yup.object()).min(1, "Patient is a required field"),
});

interface EducationShareWithDrawerProps {
  disclosureHost: DisclosureHost;
}

const EducationShareWithDrawer = ({
  disclosureHost,
}: EducationShareWithDrawerProps) => {
  const { opened, close } = disclosureHost;
  const showSnackbar = useSnackbar();
  const { mutateAsync: shareEducationMaterial } =
    useEducationMaterialShareWith();
  const queryClient = useQueryClient();
  const [searchKey, setSearchKey] = useDebounce<string>("");
  const [patientPagination, setPatientPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: patientList, isFetching: patientFetching } =
    useSearchPatientList({
      pagination: patientPagination,
      searchString: searchKey,
      xTENANTID: getXtenantId(),
    });

  const shareEducationMaterialWithPatient = async (values: any) => {
    const payload = {
      patientEducationMaterial: getUnNecessaryTransformData(["patients"], {
        ...values,
        uuid: disclosureHost.data,
      }),
      xTENANTID: getXtenantId() ?? "",
    };

    showToastOnApiCall({
      apiCall: shareEducationMaterial(payload),
      showSnackbar,
      onSave: () => {
        formik.resetForm();
        queryClient.invalidateQueries({
          queryKey: ["education-material-list"],
        });
        close();
      },
    });
  };

  const formik = useFormik<{ patients?: any[] }>({
    initialValues,
    validationSchema,
    onSubmit: shareEducationMaterialWithPatient,
  });

  return (
    <SideSheetDrawer
      w="38vw"
      open={opened}
      onClose={close}
      title="Share Education Material"
      footerActions={
        <Button
          onClick={() => formik.handleSubmit()}
          variant="contained"
          style={{ width: "100px" }}
        >
          Share
        </Button>
      }
    >
      <Flex flexDirection={"column"} alignItems={"center"} gap={2}>
        {/* <Lottie
          animationData={shareLottie}
          loop
          autoplay
          style={{ width: 300, height: 300 }}
        /> */}
        <Box p={2} mb={3}>
          <img
            src={shareIcon}
            style={{ mixBlendMode: "multiply" }}
            height={190}
            width={170}
            alt="share"
          />
        </Box>

        <Box width={"100%"}>
          <MaskMultiSelectScroll
            value={formik.values.patients ?? []}
            onChange={(value) => formik.setFieldValue("patients", value)}
            onSearch={(value) => {
              setSearchKey(value);
              setPatientPagination((p) => ({ ...p, page: 0 }));
            }}
            options={
              patientList?.data?.content?.map((it) => ({
                value: it?.uuid,
                label: it?.firstName + " " + it?.lastName,
              })) ?? []
            }
            label="Share With"
            required
            error={formik.errors.patients as string}
            pagination={patientPagination}
            setPagination={setPatientPagination}
            totalElements={patientList?.data?.page?.totalElements}
            loading={patientFetching}
          />
        </Box>
      </Flex>
    </SideSheetDrawer>
  );
};

export default EducationShareWithDrawer;
